import {
  SearchValue,
  SearchValueShow,
} from '@/components/ActiveSearchTags/types';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { DATE_FORMAT, format } from 'date-util';
import { useCallback, useMemo } from 'react';
import { useInvoiceValueRetrieval } from './useInvoiceValueRetrieval';

// ============================================================================
// Types
// ============================================================================

export type GenericFieldOrder<TKey extends string = string> = {
  key: TKey | TKey[];
  label: string;
  concatChar?: string;
  isDateField?: boolean;
  isAmountField?: boolean;
  isBooleanField?: boolean;
  booleanTrueLabel?: string;
  canShowPartialValue?: boolean;
};

export type ValueTransformer = (
  value: string | string[]
) => string | (string | undefined)[];

export type ValueRetrievalFunctions = {
  getApPayeeValue: (value: string, options?: BaseOption[]) => string;
  getDeptValues: (values: string[], options?: BaseOption[]) => string[];
  getOfficeMemberValues: (values: string[], options?: BaseOption[]) => string[];
  getInvoiceStatusValues?: (value: string, options?: BaseOption[]) => string;
  getInvoiceFilesStatusValues?: (params: {
    value: string;
    invoiceFilesStatusOptions?: BaseOption[];
    isHideIfNoLabel?: boolean;
  }) => string;
};

export type UseShowSearchValueProps<
  TQuery extends Record<string, unknown> = Record<string, unknown>
> = {
  /** Accepts any object (e.g. OpenAPI query types without index signature) */
  searchQuery?: TQuery | object;
  fieldOrders: GenericFieldOrder[];
  excludeFields?: string[];
  alwaysShowFields?: string[];
  payeeOptions?: BaseOption[];
  deptOptions?: BaseOption[];
  reportDeptOptions?: BaseOption[];
  memberOptions?: BaseOption[];
  customTransformers?: Record<string, ValueTransformer>;
  valueRetrievalFunctions?: ValueRetrievalFunctions;
};

// ============================================================================
// Type Guards & Helpers
// ============================================================================

const isString = (value: unknown): value is string => typeof value === 'string';

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(isString);

/** Convert unknown value to SearchValue type */
const toSearchValue = (value: unknown): SearchValue => {
  if (value === null || value === undefined) return undefined;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
    return value;
  if (isStringArray(value)) return value;
  return undefined;
};

/** Check if field has a value in query */
const fieldHasValue = (
  key: string | string[],
  query: Record<string, unknown>,
  allowPartial?: boolean
): boolean => {
  if (typeof key === 'string') return Boolean(query[key]);
  return allowPartial
    ? key.some((k) => Boolean(query[k]))
    : key.every((k) => Boolean(query[k]));
};

/** Normalize to Record for internal use (handles object types without index signature) */
const toQueryRecord = (input: object | undefined): Record<string, unknown> => {
  if (!input) return {};
  return Object.fromEntries(
    Object.entries(input).map(([k, v]): [string, unknown] => [k, v])
  );
};

/** Check if field should be excluded */
const isFieldExcluded = (
  key: string | string[],
  excludeList: string[]
): boolean => {
  if (!excludeList.length) return false;
  return Array.isArray(key)
    ? excludeList.some((e) => key.includes(e))
    : excludeList.includes(key);
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to transform search query into displayable tag values
 *
 * @example
 * const { searchValuesShow } = useShowSearchValue({
 *   searchQuery,
 *   fieldOrders: FIELD_ORDERS,
 *   payeeOptions,
 * });
 */
export const useShowSearchValue = <
  TQuery extends Record<string, unknown> = Record<string, unknown>
>(
  props: UseShowSearchValueProps<TQuery>
) => {
  const {
    searchQuery,
    fieldOrders = [],
    excludeFields = [],
    alwaysShowFields = ['status'],
    payeeOptions = [],
    deptOptions = [],
    reportDeptOptions = [],
    memberOptions = [],
    customTransformers = {},
    valueRetrievalFunctions,
  } = props;

  const query = useMemo<Record<string, unknown>>(
    () => toQueryRecord(searchQuery),
    [searchQuery]
  );
  const defaultRetrieval = useInvoiceValueRetrieval();
  const retrieval = valueRetrievalFunctions ?? defaultRetrieval;
  const { t } = useTranslation();

  // Build field transformers map
  const transformers = useMemo<Record<string, ValueTransformer>>(
    () => ({
      ap_payee_id: (v) =>
        retrieval.getApPayeeValue(isString(v) ? v : '', payeeOptions),
      dept_ids: (v) =>
        retrieval.getDeptValues(isStringArray(v) ? v : [], deptOptions),
      report_dept_ids: (v) =>
        retrieval.getDeptValues(isStringArray(v) ? v : [], reportDeptOptions),
      office_member_ids: (v) =>
        retrieval.getOfficeMemberValues(
          isStringArray(v) ? v : [],
          memberOptions
        ),
      status: (v) =>
        retrieval.getInvoiceStatusValues?.(isString(v) ? v : '') ??
        (isString(v) ? v : ''),
      mf_file: (v) =>
        retrieval.getInvoiceFilesStatusValues?.({
          value: isString(v) ? v : '',
          isHideIfNoLabel: true,
        }) ?? '',
      ...customTransformers,
    }),
    [
      retrieval,
      payeeOptions,
      deptOptions,
      reportDeptOptions,
      memberOptions,
      customTransformers,
    ]
  );

  // Filter to active fields only
  const activeFields = useMemo(
    () =>
      fieldOrders.filter((field) => {
        if (!field?.key) return false;
        const isAlwaysVisible =
          typeof field.key === 'string' && alwaysShowFields.includes(field.key);
        const hasValue =
          isAlwaysVisible ||
          fieldHasValue(field.key, query, field.canShowPartialValue);
        return hasValue && !isFieldExcluded(field.key, excludeFields);
      }),
    [fieldOrders, query, alwaysShowFields, excludeFields]
  );

  // Transform field to display value
  const transformField = useCallback(
    (field: GenericFieldOrder): SearchValue | SearchValue[] => {
      const { key } = field;

      // Single key field
      if (typeof key === 'string') {
        const value =
          key === 'status' && query[key] === undefined ? '' : query[key];

        // Apply transformer if exists
        const transformer = transformers[key];
        if (transformer && (isString(value) || isStringArray(value))) {
          return transformer(value);
        }

        // Handle boolean fields
        if (typeof value === 'boolean' && field.isBooleanField) {
          return value ? field.booleanTrueLabel ?? t('checked') : '';
        }

        return toSearchValue(value);
      }

      // Multi-key field (date range, amount range, etc.)
      const values = key.map((k) => query[k]);

      if (field.isDateField) {
        return values.map((v) =>
          isString(v) ? format(v, DATE_FORMAT) : toSearchValue(v)
        );
      }

      if (field.isAmountField) {
        return values.map((v) =>
          isString(v) ? toCommaSeparated(v) : toSearchValue(v)
        );
      }

      return values.map(toSearchValue);
    },
    [query, transformers, t]
  );

  // Build output
  const searchValuesShow = useMemo<SearchValueShow[]>(
    () =>
      activeFields.map((field) => ({
        fieldLabel: field.label,
        valueShow: transformField(field),
        concatChar: field.concatChar,
      })),
    [activeFields, transformField]
  );

  return { searchValuesShow };
};
